import { match, P } from 'ts-pattern';
import { z } from 'zod';
import type { PartialOnUndefined } from '$lib/utils/types';
import { type CurrentParticipantInput, type ParticipantInput } from '$gql/graphql.js';

type Participant =
    | { __typename: 'Company'; id: number }
    | { __typename: 'User'; id: number };

export const zParticipantInput = z
    .object({
        userId: z.number().nullish(),
        companyId: z.number().nullish(),
    })
    .transform((val, ctx) => {
        if (val.companyId == null && val.userId == null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Participant Input is empty',
            });
            return z.NEVER;
        }

        return val;
    });

export const zCurrentParticipantInput = z
    .object({
        userId: z.number().nullish(),
        companyId: z.number().nullish(),
        all: z.boolean().nullish(),
    })
    .transform((val, ctx) => {
        if (val.companyId == null && val.userId == null && val.all == null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'CurrentParticipantInput is empty',
            });
            return z.NEVER;
        }

        return val;
    });

export type zParticipantInput = z.infer<typeof zParticipantInput>;

export function participantToInput(p: Participant): ParticipantInput {
    if (p.__typename === 'User') {
        return { userId: p.id };
    } else if (p.__typename === 'Company') {
        return { companyId: p.id };
    }

    throw new Error('invalid participant');
}

export function participantToInputOrNull(p: Participant | null): ParticipantInput | null {
    return p == null ? null : participantToInput(p);
}

export function participantInputToString(p: ParticipantInput): string {
    return matchParticipantInput(p, {
        user: (userId) => `User:${userId}`,
        company: (companyId) => `Company:${companyId}`,
    });
}

export function participantInputAreEqual(
    p1: ParticipantInput,
    p2: ParticipantInput,
): boolean {
    const p1Company = p1.companyId ?? null;
    const p2Company = p2.companyId ?? null;
    const p1User = p1.userId ?? null;
    const p2User = p2.userId ?? null;

    return p1Company === p2Company && p1User === p2User;
}

export function currentParticipantInputAreEqual(
    p1: CurrentParticipantInput,
    p2: CurrentParticipantInput,
): boolean {
    const p1Company = p1.companyId ?? null;
    const p2Company = p2.companyId ?? null;
    const p1User = p1.userId ?? null;
    const p2User = p2.userId ?? null;
    const p1All = p1.all ?? null;
    const p2All = p2.all ?? null;

    return p1Company === p2Company && p1User === p2User && p1All === p2All;
}

export function participantAreEqual(
    p1: Participant | null | undefined,
    p2: Participant | null | undefined,
): boolean {
    if (p1 == null && p2 == null) {
        return true;
    }
    if (p1 == null || p2 == null) {
        return false;
    }
    return p1.__typename === p2.__typename && p1.id === p2.id;
}

export function participantIsUser(participant: Participant | null | undefined) {
    return participant?.__typename === 'User';
}

export function participantIsCompany(participant: Participant | null | undefined) {
    return participant?.__typename === 'Company';
}

export function participantSveltekitReload(participant: Participant): boolean {
    return participantIsCompany(participant);
}

export function participantInputToCurrentParticipantInput(
    participant: ParticipantInput,
): CurrentParticipantInput {
    return { all: null, ...participant };
}

export function matchParticipantInput<A, B = A>(
    p: PartialOnUndefined<ParticipantInput>,
    callbacks: { user: (id: number) => A; company: (id: number) => B },
): A | B {
    return match(p)
        .with({ userId: P.number }, ({ userId }) => callbacks.user(userId))
        .with({ companyId: P.number }, ({ companyId }) => callbacks.company(companyId))
        .run();
}
